import { getAxiosCached } from '@/helpers/axios-helper';
import { ObjectHelper } from '@/helpers/object-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import Genre from '@/interfaces/genre';
import Provider from '@/interfaces/provider';
import { OfferMonetizationType } from '@/interfaces/titles';
import { FreezingItems } from '@/store';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

/* GRAPHQL CONSTANTS */
import { GetAgeCertificationsQuery } from '@/pages/graphql/queries/GetAgeCertifications.query';

/** Use this symbol to Inject and Provide this constant. */
export const AgeCertificationsConstant = Symbol('AgeCertifications');
export type AgeCertifications = {
	hasCertifications: boolean;
	organizations: string[];
	movies: GetAgeCertificationsQuery['movies'];
	shows: GetAgeCertificationsQuery['shows'];
};
/*********************/

export type ConstantState = {
	genres: Genre[];
	providers: Provider[];
};

type ConstantGetter<T> = (state: ConstantState) => T;

export type ConstantGetters = {
	genres: ConstantGetter<ConstantState['genres']>;
	genresById: ConstantGetter<Record<string, Genre>>;
	genresByShortName: ConstantGetter<Record<string, Genre>>;
	genresBySlug: ConstantGetter<Record<string, Genre>>;
	providers: ConstantGetter<Provider[]>;
	providersById: ConstantGetter<Record<string, Provider>>;
	providersByShortName: ConstantGetter<Record<string, Provider>>;
	providersBySlug: ConstantGetter<Record<string, Provider>>;
};

export type ConstantStore = {
	state: ConstantState;
	getters: ConstantGetters;
};

export enum ConstantType {
	PROVIDERS = 'providers',
	GENRES = 'genres',
}

export const CONSTANT_INIT_FREEZE_ITEMS: FreezingItems<ConstantState> = {
	'providers|genres': state => {
		(['providers', 'genres'] as (keyof ConstantState)[])
			.map(key => state[key])
			.forEach((item: any) => Object.freeze(item));
	},
};

const state = () =>
	({
		providers: [],
		genres: [],
	} as ConstantState);

// GETTERS
const getters: GetterTree<ConstantState, any> = {
	/**
	 * visible providers for the provider filter bar.
	 */
	providers:
		(state, getters, rootState, rootGetters) =>
		(filterByUserProviders = true) => {
			let filteredProviders = state.providers || [];

			let { providers: userProviders = [] } = rootState.user.settings;

			// cheap trick for the wrong serialization userProviders on the server is an object instad of an array
			// loggedInProviders is also affected
			if (!(userProviders instanceof Array)) {
				userProviders = Object.values(userProviders);
			}

			if (filterByUserProviders && userProviders.length) {
				filteredProviders = filteredProviders.filter(provider => userProviders.includes(provider.short_name));
			}

			const freeProvider = filteredProviders.find(({ short_name }) => short_name === 'fre') || null;

			if (freeProvider && rootGetters['user/isLoggedIn']()) {
				const freeMonetizationTypes = [OfferMonetizationType.FREE, OfferMonetizationType.ADS];

				filteredProviders = [
					...filteredProviders.filter((provider: Provider) =>
						provider.monetization_types?.some(
							monetizationType => !freeMonetizationTypes.includes(monetizationType)
						)
					),
					freeProvider,
				];
			}

			return filteredProviders;
		},
	providersById(state): Record<number, Provider> {
		return ObjectHelper.pluck(state.providers, 'id');
	},
	providersByShortName(state): Record<string, Provider> {
		return ObjectHelper.pluck(state.providers, 'short_name');
	},
	providersBySlug: (state): Record<string, Provider> => {
		return ObjectHelper.pluck(state.providers, 'slug');
	},
	/**
	 * get first three providers of the region to preselect for a new user
	 */
	preselectedProviders: (state, getters, rootState): string[] => {
		const { providers: userProviders = [] } = rootState.user.settings;
		return userProviders.length > 0
			? userProviders
			: (getters.providers(false) as Provider[]).slice(0, 3).map(provider => provider.short_name);
	},
	genres(state): Genre[] {
		return state.genres;
	},
	genresById(state): Record<number, Genre> {
		return ObjectHelper.pluck(state.genres, 'id');
	},
	genresByShortName(state): Record<string, Genre> {
		return ObjectHelper.pluck(state.genres, 'short_name');
	},
	genresBySlug(state): Record<string, Genre> {
		return ObjectHelper.pluck(state.genres, 'slug');
	},
};

// ACTIONS
const actions: ActionTree<ConstantState, any> = {
	async fetchConstant({ commit, rootGetters }, constantType: ConstantType) {
		const locale = rootGetters['language/locale'];
		const overridenLocale = rootGetters['language/overriddenLocale'];
		try {
			if (!locale && !overridenLocale) {
				throw new Error('[Constant Store] locale not set');
			}
			// default url and params for most of constant types
			let url = `${JW_CONFIG.API}/${constantType}/locale/${overridenLocale}`;
			let params = {};

			if (constantType === ConstantType.PROVIDERS) {
				url = `${JW_CONFIG.API}/providers/locale/${locale}`;
			}
			// note: caching or hardcoding constants does not yield any speed improvements at all (on wifi at least)
			const result = await getAxiosCached().get(url, { params });
			const { data } = result;
			if (!data || !Array.isArray(data)) {
				throw new Error(`Unable to load constants "${constantType}" from API or wrong payload`);
			}

			// decide what to do with the result
			switch (constantType) {
				case ConstantType.PROVIDERS:
					commit('SET_PROVIDERS', data);
					break;
				case ConstantType.GENRES:
					commit('SET_GENRES', data as Genre[]);
					break;
				default:
					throw new Error(`Unable to determine mutation for constant provider "${constantType}"`);
			}
		} catch (error: any) {
			// hard fail set App error state
			this.dispatch('app/setError', error);
			captureMessageForSentry('App Error', {
				where: `[Constant.store.ts] fetchConstant ${constantType}`,
				error,
			});
		}
	},
};

// MUTATIONS
const mutations: MutationTree<ConstantState> = {
	SET_PROVIDERS(state, providers: Provider[]) {
		// order the providers by their display prioriy
		const items = [...providers]
			.sort((a: Provider, b: Provider) => {
				return a.display_priority - b.display_priority;
			})
			.map(item => Object.freeze(item));
		state.providers = items;
	},
	SET_GENRES(state, genres: Genre[]) {
		state.genres = genres.map(genre => Object.freeze(genre));
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<ConstantState, any>;
